<div mat-dialog-content class="set-access-enhanced-dialog">
  <!-- Sticky Header -->
  <div class="sticky-header">
    <hr class="popup-top-line" />
    <div class="dialog-header">
      <div class="header-title">
        <lucide-icon name="key-round" [size]="16" [strokeWidth]="2" [color]="'#6E32E0'"></lucide-icon>
        <h2 class="title">{{ 'commonActions.manageAccessAction.setAccessTitle' | labelsProvider }}</h2>
      </div>
      <img class="close-button" src="assets/images/common/cancel.svg" (click)="close()" />
    </div>
  </div>

  <!-- Scrollable Content -->
  <div class="scrollable-content">
    <!-- Loading State -->
    @if (isInitialLoading()) {
      <div class="loading-container">
        <mat-progress-spinner color="primary" mode="indeterminate" [diameter]="50"></mat-progress-spinner>
      </div>
    } @else {
      <!-- Search Input -->
      <div class="search-section">
        <mat-form-field appearance="outline" class="search-field">
          <mat-icon matPrefix>search</mat-icon>
          <input
            matInput
            type="text"
            [placeholder]="'commonActions.manageAccessAction.searchPlaceholder' | labelsProvider"
            (input)="onSearchInput($event)"
            [disabled]="isLoading() || isBulkUpdating()"
          />
        </mat-form-field>
      </div>

      <!-- Bulk Action Row -->
      @if (users().length > 0) {
        <div class="bulk-selection-row">
          <span class="bulk-label">
            {{ 'commonActions.manageAccessAction.setAllUsersRole' | labelsProvider }}
            ({{ users().length }} {{ 'commonActions.manageAccessAction.users' | labelsProvider }})
          </span>

          <mat-form-field appearance="outline" class="bulk-role-select" #bulkSelectField>
            <mat-select
              [placeholder]="'commonActions.manageAccessAction.selectRole' | labelsProvider"
              (selectionChange)="onBulkRoleChange($event.value)"
              (openedChange)="$event ? onSelectOpened(bulkSelectField) : onSelectClosed()"
              [disabled]="!canBulkEdit() || isLoading() || isBulkUpdating()"
              [matTooltip]="bulkEditDisabledReason()"
              [matTooltipDisabled]="canBulkEdit()"
              panelClass="set-access-select-panel"
            >
              @for (option of bulkRoleOptions(); track option.value) {
                <mat-option [value]="option.value">
                  {{ option.label }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      }

      <!-- User List -->
      <div class="user-list">
        @if (filteredUsers().length === 0) {
          <div class="empty-state">
            @if (searchTerm()) {
              <p>{{ 'commonActions.manageAccessAction.noUsersFound' | labelsProvider }}</p>
            } @else {
              <p>{{ 'commonActions.manageAccessAction.noUsersWithAccess' | labelsProvider }}</p>
            }
          </div>
        } @else {
          @for (user of filteredUsers(); track trackByUserId($index, user)) {
            <div class="user-row" [class.updating]="user.isUpdating">
              <div class="user-avatar">
                <div class="avatar-circle">
                  {{ getUserInitials(user) }}
                </div>
              </div>

              <div
                class="user-info"
                [appTruncateTooltip]="getUserTooltip(user)"
                truncateCheckSelector=".user-email"
                matTooltipShowDelay="500"
              >
                <div class="user-name-email">
                  @if (user.displayName) {
                    <span class="user-name">{{ user.displayName }}</span>
                    <span class="user-email">({{ user.email || user.identityPlatformResourceId }})</span>
                  } @else {
                    <span class="user-email">{{ user.email || user.identityPlatformResourceId }}</span>
                  }
                </div>
              </div>

              <div class="role-section">
                @if (user.isUpdating || isBulkUpdating()) {
                  <mat-progress-spinner color="primary" mode="indeterminate" [diameter]="20"> </mat-progress-spinner>
                } @else {
                  <mat-form-field appearance="outline" class="role-select" #roleSelectField>
                    <mat-select
                      [value]="user.role"
                      (selectionChange)="onIndividualRoleChange(user, $event.value)"
                      (openedChange)="$event ? onSelectOpened(roleSelectField) : onSelectClosed()"
                      [disabled]="isLoading() || isBulkUpdating()"
                      panelClass="set-access-select-panel"
                    >
                      <mat-select-trigger>
                        <span
                          class="role-trigger-text"
                          [appTruncateTooltip]="getRoleLabel(user.role)"
                          matTooltipShowDelay="500"
                        >
                          {{ getRoleLabel(user.role) }}
                        </span>
                      </mat-select-trigger>
                      @for (option of roleOptions; track option.value) {
                        <mat-option [value]="option.value">
                          {{ option.label }}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                }
              </div>
            </div>
          }
        }
      </div>
    }
  </div>

  <!-- Sticky Add User Block -->
  <div class="sticky-add-user">
    <mat-form-field appearance="outline" class="add-user-email-field">
      <input
        matInput
        type="email"
        [placeholder]="'commonActions.manageAccessAction.addUserEmailPlaceholder' | labelsProvider"
        [formControl]="emailFormControl"
      />
    </mat-form-field>

    <mat-form-field appearance="outline" class="add-user-role-select" #addUserRoleSelectField>
      <mat-select
        [placeholder]="'commonActions.manageAccessAction.selectRole' | labelsProvider"
        [value]="newUserRole()"
        (selectionChange)="onNewUserRoleChange($event.value)"
        (openedChange)="$event ? onSelectOpened(addUserRoleSelectField) : onSelectClosed()"
        [disabled]="isAddingUser()"
        panelClass="set-access-select-panel"
      >
        @for (option of bulkRoleOptions(); track option.value) {
          <mat-option [value]="option.value">
            {{ option.label }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <button
      class="add-user-button"
      [ngClass]="{ 'add-user-button-disabled': !isAddUserValid() || isAddingUser() }"
      [disabled]="!isAddUserValid() || isAddingUser()"
      (click)="onAddUser()"
    >
      @if (isAddingUser()) {
        <mat-progress-spinner color="accent" mode="indeterminate" [diameter]="18"></mat-progress-spinner>
      } @else {
        {{ 'commonActions.manageAccessAction.addUser' | labelsProvider }}
      }
    </button>
  </div>
</div>

<!-- Dialog Actions -->
<div mat-dialog-actions class="dialog-actions">
  <button class="close-button" [disabled]="isLoading() || isBulkUpdating()" (click)="close()">
    {{ 'commonActions.manageAccessAction.close' | labelsProvider }}
  </button>

  <button
    class="isolate-button"
    [ngClass]="{ 'isolate-button-disabled': !canIsolateResource() || isLoading() || isBulkUpdating() }"
    [disabled]="!canIsolateResource() || isLoading() || isBulkUpdating()"
    (click)="isolateResource()"
  >
    @if (isLoading()) {
      <mat-progress-spinner color="accent" mode="indeterminate" [diameter]="18"> </mat-progress-spinner>
    } @else {
      <lucide-icon name="box" [size]="16" [strokeWidth]="2" color="currentColor"></lucide-icon>
      {{ 'commonActions.manageAccessAction.isolateResource' | labelsProvider }}
    }
  </button>
</div>
